import * as React from 'react'
import { useDiagnosticsApi, useInvokeApi, useAuth } from '../../hooks'
import { Formik } from 'formik'
import { FormikSelect, EditModalActionBar } from '../../components'
import { Form, Box, Loader } from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { Grid } from 'cs-kevlar-react'
import {
  DiagnosticConfigurationViewModel,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { getAllEnumOptions } from '../../utils'

interface EditNebulaLogLevelsProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
  }),
)

enum LogEventLevel {
  Verbose = 0,
  Debug = 1,
  Information = 2,
  Warning = 3,
  Error = 4,
  Fatal = 5,
}

const logLevels: Option[] = getAllEnumOptions(LogEventLevel)

export const EditNebulaLogLevels: React.FunctionComponent<EditNebulaLogLevelsProps> = ({
  onClose,
}) => {
  const {
    getDiagnosticsConfiguration,
    updateDiagnosticsConfiguration,
  } = useDiagnosticsApi()
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const [
    diagnosticsConfiguration,
    setDiagnosticsConfiguration,
  ] = React.useState<DiagnosticConfigurationViewModel | null>(null)

  React.useEffect(() => {
    invokeApi(
      () => getDiagnosticsConfiguration(),
      'There was an error getting the diagnostic configuration',
      result => {
        setDiagnosticsConfiguration(result)
      },
    )
  }, [])

  if (authUser === null) return null

  const onEdit = (values: DiagnosticConfigurationViewModel, actions: any) => {
    invokeApi(
      () => updateDiagnosticsConfiguration(values),
      'There was an error setting the diagnostic configuration',
      _ => {},
      () => {
        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageDiagnosticConfiguration,
    )

  return (
    <React.Fragment>
      {diagnosticsConfiguration === null && (
        <Loader size="sm" text="Loading Nebula Log Levels" />
      )}
      {diagnosticsConfiguration !== null && (
        <Formik
          initialValues={diagnosticsConfiguration}
          validationSchema={null}
          onSubmit={onEdit}
        >
          {formikProps => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column">
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="GlobalLogLevelOverride"
                      fieldName="globalRuntimeLogLevelOverride"
                      label="Global"
                      options={logLevels}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="NebulaAPIRuntimeLogLevelOverride"
                      fieldName="nebulaAPIRuntimeLogLevelOverride"
                      label="Nebula API"
                      options={logLevels}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="NebulaOrleansHostRuntimeLogLevelOverride"
                      fieldName="nebulaOrleansHostRuntimeLogLevelOverride"
                      label="Orleans Host"
                      options={logLevels}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="NebulaIdentityServerRuntimeLogLevelOverride"
                      fieldName="nebulaIdentityServerRuntimeLogLevelOverride"
                      label="Identity Server"
                      options={logLevels}
                      fullWidth
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="NebulaWebAdminRuntimeLogLevelOverride"
                      fieldName="nebulaWebAdminRuntimeLogLevelOverride"
                      label="Web Admin"
                      options={logLevels}
                      fullWidth
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  canSubmit={canEdit}
                  formikProps={formikProps}
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
